import { useRef, useEffect } from 'react';
import { useConversationMessages, usePatchConversation } from './useConversations';
import { useWhatsapp } from '../store/whatsapp';
import type { WhatsappConversation } from '@marnic/shared';

interface Props {
  conversation: WhatsappConversation | null;
}

const STATUS_ICON: Record<string, string> = {
  queued: '○',
  sent: '✓',
  delivered: '✓✓',
  read: '✓✓',
  failed: '✗',
};

export function WhatsappChatPanel({ conversation }: Props): JSX.Element {
  const endRef = useRef<HTMLDivElement>(null);
  const messagesByConversation = useWhatsapp((s) => s.messagesByConversation);
  const markRead = useWhatsapp((s) => s.markConversationRead);

  const convId = conversation?.id ?? null;
  const { isLoading } = useConversationMessages(convId);
  const { mutate: patchConv } = usePatchConversation(convId ?? '');

  const messages = convId ? (messagesByConversation[convId] ?? []) : [];

  useEffect(() => {
    if (convId && conversation && conversation.unreadCount > 0) {
      markRead(convId);
    }
  }, [convId, conversation, markRead]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length]);

  if (!conversation) {
    return (
      <div className="rounded-md border border-dashed border-ink-200 p-8 text-center text-sm text-ink-400">
        Select a conversation to open the chat.
      </div>
    );
  }

  return (
    <div className="card flex flex-col" style={{ height: '600px' }}>
      {/* Header */}
      <div className="shrink-0 border-b border-ink-200 px-4 py-3 flex items-center justify-between">
        <div>
          <div className="font-medium text-sm">
            {conversation.lead?.firstName} {conversation.lead?.lastName}
            {conversation.lead?.company ? ` · ${conversation.lead.company}` : ''}
          </div>
          <div className="text-xs text-ink-500 font-mono">{conversation.lead?.phoneE164}</div>
        </div>
        {conversation.status === 'open' && (
          <button
            type="button"
            className="btn-secondary text-xs"
            onClick={() => patchConv({ status: 'closed' })}
          >
            Close
          </button>
        )}
        {conversation.status !== 'open' && (
          <button
            type="button"
            className="btn-secondary text-xs"
            onClick={() => patchConv({ status: 'open' })}
          >
            Reopen
          </button>
        )}
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-2">
        {isLoading && messages.length === 0 && (
          <div className="text-center text-xs text-ink-400 pt-8">Loading messages…</div>
        )}
        {!isLoading && messages.length === 0 && (
          <div className="text-center text-xs text-ink-400 pt-8">No messages yet.</div>
        )}
        {messages.map((m) => {
          const isOut = m.direction === 'outbound';
          return (
            <div key={m.id} className={`flex ${isOut ? 'justify-end' : 'justify-start'}`}>
              <div
                className={`max-w-[75%] rounded-2xl px-3 py-2 text-sm ${
                  isOut
                    ? 'bg-brand-600 text-white rounded-br-sm'
                    : 'bg-ink-100 text-ink-900 rounded-bl-sm'
                }`}
              >
                <p className="whitespace-pre-wrap break-words">{m.body}</p>
                {m.mediaUrl && (
                  <a
                    href={m.mediaUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="text-xs underline opacity-70 mt-1 block"
                  >
                    View media
                  </a>
                )}
                <div
                  className={`flex items-center gap-1 mt-1 text-xs ${
                    isOut ? 'text-white/70 justify-end' : 'text-ink-500'
                  }`}
                >
                  <span>
                    {new Date(m.createdAt).toLocaleTimeString('en-GB', {
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </span>
                  {isOut && (
                    <span className={m.status === 'read' ? 'text-blue-300' : ''}>
                      {STATUS_ICON[m.status] ?? '○'}
                    </span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
        <div ref={endRef} />
      </div>

      {/* Coexistence banner — composer removed; replies are sent from the
          WhatsApp Business App on the phone and mirrored here. */}
      <div className="shrink-0 border-t border-ink-200 bg-ink-50 px-4 py-3 text-center text-xs text-ink-600">
        Reply from the WhatsApp app on the phone — messages will appear here automatically.
      </div>
    </div>
  );
}
